import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { apiClient, authApi } from '@/lib/api'
import type { ImpersonatedBy, UserProfile } from '@/types/domain'

interface AuthState {
  user: UserProfile | null
  /** True until the bootstrap refresh has answered, so a protected route
   *  does not bounce a signed-in person to the login screen on reload. */
  loading: boolean
  signedIn: boolean
  /** Set for the duration of a staff impersonation session — see
   *  `beginImpersonation`. `null` for an ordinary sign-in. */
  impersonatedBy: ImpersonatedBy | null
  impersonationExpiresAt: string | null
  /**
   * True once an impersonation session's token has expired or been refused
   * (a 401 with no refresh to fall back to). Distinct from `!signedIn`
   * alone: `ProtectedRoute` reads this to show "Ansicht beendet" in place
   * rather than bouncing to `/login`, which would read as an ordinary
   * sign-out of an account that was never signed in to begin with.
   */
  impersonationEnded: boolean
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>
  register: (input: {
    email: string
    password: string
    firstName: string
    lastName?: string
  }) => Promise<void>
  signInWithGoogleToken: (idToken: string) => Promise<void>
  completeOAuthCode: (code: string) => Promise<void>
  logout: () => Promise<void>
  reload: () => Promise<void>
  /** Stands up a read-only, 30-minute session from a staff member's
   *  one-time impersonation code (`/auth/impersonate#code=…`). No refresh
   *  token, no cookie — see the module doc below. */
  beginImpersonation: (code: string) => Promise<void>
  /** The one write the read-only middleware still allows during
   *  impersonation (`POST /v1/auth/logout` answers 204, nothing to revoke):
   *  ends the session locally and tells the gateway, without touching the
   *  refresh cookie — there is none to touch. */
  endImpersonation: () => Promise<void>
  /** "In der Nutzeransicht kann nichts geändert werden." — set whenever a
   *  write is refused with `impersonation_read_only`, wherever in the app it
   *  was attempted. `null` otherwise. */
  impersonationToast: string | null
  dismissImpersonationToast: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Session handling, ported from the Flutter app's `AuthClient` plus its
 * bootstrap in `main.dart`.
 *
 * The access token lives in `apiClient`'s memory and nowhere else. On every
 * app start this asks the gateway to trade the http-only refresh cookie for
 * a fresh one; there is nothing in `localStorage` to go stale, to leak into
 * a request after a logout elsewhere, or to be read by anything running on
 * the page.
 */

/**
 * The refresh exchange, run once at a time — for the app start and for the
 * 401 retry alike.
 *
 * `POST /v1/auth/refresh` rotates the cookie and treats a second call
 * carrying the consumed one as **reuse**: the gateway revokes the whole
 * token family and every session in it. Two tabs restored together (session
 * restore, cmd-click, duplicate tab) would each send the same cookie, and in
 * development `StrictMode` fires the effect twice in one tab — so the
 * exchange is serialised twice over: a module-level in-flight promise for
 * this document, and the Web Locks API across the tabs of one browser.
 * `apiClient`'s own single-flight only covers the 401-retry path.
 *
 * Serialising is enough; the waiting tab does not need to be told the answer.
 * The cookie jar is shared and the browser reads it when it sends, so once
 * the first exchange's `Set-Cookie` has landed, the tab that was waiting on
 * the lock carries the *new* cookie — an ordinary rotation, not a reuse.
 */
let bootstrapInFlight: Promise<string | null> | null = null

function bootstrapRefresh(): Promise<string | null> {
  if (bootstrapInFlight) return bootstrapInFlight
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  const promise: Promise<string | null> = locks?.request
    ? Promise.resolve(locks.request('fx-auth-bootstrap-refresh', () => authApi.refresh()))
    : authApi.refresh()
  bootstrapInFlight = promise
  const clear = () => {
    if (bootstrapInFlight === promise) bootstrapInFlight = null
  }
  void promise.then(clear, clear)
  return promise
}
const NOT_IMPERSONATING = {
  impersonatedBy: null,
  impersonationExpiresAt: null,
  impersonationEnded: false,
} as const

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    signedIn: false,
    ...NOT_IMPERSONATING,
  })
  const [impersonationToast, setImpersonationToast] = useState<string | null>(null)
  const queryClient = useQueryClient()
  /**
   * Cached server state belongs to the session that filled it. The client is
   * a singleton that outlives a sign-out, so without this the next person to
   * sign in on the same tab is served the previous account's forms, counts
   * and quota out of the cache while the refetch is still in flight.
   */
  const forgetServerState = useCallback(() => {
    queryClient.clear()
  }, [queryClient])
  // The refresher is registered with apiClient once; it must not be rebuilt
  // on every render or a 401 mid-refresh would call a stale closure.
  const sessionExpired = useRef(false)
  /**
   * Mirrors `state.impersonatedBy != null`, but as a ref: `onRefresh` is a
   * closure registered once (see above) and must see the *current* value,
   * not the one captured when `configure` last ran.
   */
  const impersonating = useRef(false)

  useEffect(() => {
    apiClient.configure({
      onRefresh: async () => {
        // An impersonation session has no refresh token and no cookie —
        // there is nothing here to exchange. Returning null immediately
        // (rather than calling `bootstrapRefresh`, which would 401 against
        // whatever cookie this browser happens to carry — possibly the
        // staff member's own, in another tab) sends every 401 straight to
        // `onSessionExpired`.
        if (impersonating.current) return null
        // Through the same guard as the bootstrap: `apiClient`'s single-flight
        // only covers 401s within this tab, so a 401 arriving while another
        // tab is mid-exchange would otherwise send the cookie a second time
        // and be read as reuse.
        const token = await bootstrapRefresh()
        apiClient.setAccessToken(token)
        return token
      },
      onSessionExpired: () => {
        sessionExpired.current = true
        const wasImpersonating = impersonating.current
        impersonating.current = false
        apiClient.setAccessToken(null)
        forgetServerState()
        setState({
          user: null,
          loading: false,
          signedIn: false,
          ...NOT_IMPERSONATING,
          // "Ansicht beendet", not a bounce to /login: this session was
          // never a real sign-in for this browser to begin with.
          impersonationEnded: wasImpersonating,
        })
      },
      onImpersonationBlocked: () => {
        setImpersonationToast('In der Nutzeransicht kann nichts geändert werden.')
      },
    })
  }, [forgetServerState])

  const loadUser = useCallback(async () => {
    const user = await authApi.me()
    setState((current) => ({ ...current, user, loading: false, signedIn: user != null }))
  }, [])

  /**
   * Take up a freshly issued token. The cache is emptied *before* the first
   * call of the new session: the tab may still hold what the previous account
   * — or the anonymous funnel — put there, and a screen that mounts while
   * `/me` is in flight would otherwise render those entries as if they were
   * the new person's.
   */
  const beginSession = useCallback(
    async (token: string | null) => {
      impersonating.current = false
      forgetServerState()
      apiClient.setAccessToken(token)
      await loadUser()
      setState((current) => ({ ...current, ...NOT_IMPERSONATING }))
    },
    [forgetServerState, loadUser],
  )

  // Bootstrap: one refresh, then one /me. Both are allowed to fail — that
  // just means nobody is signed in.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      // `/auth/impersonate` mounts inside this same provider, and its effect
      // runs *before* this one (React fires a child's effects ahead of its
      // parent's on the same commit) — so if `beginImpersonation` is already
      // under way, `impersonating.current` is already true by the time this
      // line runs, and there is nothing for the ordinary bootstrap to do.
      // Without this guard, a staff member who also happens to be signed in
      // as themselves on this same origin — plausible, since the
      // impersonation link opens the cockpit's own domain — has their
      // *own* refresh cookie exchanged here concurrently, and whichever
      // network call answers last silently overwrites the impersonated
      // session with the staff member's real one. Reproduced locally: the
      // banner read "Ansicht als staff@example.com" instead of the
      // impersonated account.
      if (impersonating.current) return
      // A token already in memory means this provider is remounting inside a
      // session that is already running (a route swap, a re-render of the
      // app shell) — including an impersonation session already stood up by
      // `beginImpersonation`. Rotating the cookie again buys nothing and is
      // one more exchange that could race another tab; for an impersonation
      // session it would also be wrong, since there is no cookie for it.
      const existing = apiClient.getAccessToken()
      if (existing) {
        if (!cancelled && !impersonating.current) await loadUser()
        return
      }
      const token = await bootstrapRefresh()
      // Re-checked after the await: `beginImpersonation` may have started
      // (and even finished) while this refresh was in flight.
      if (cancelled || impersonating.current) return
      apiClient.setAccessToken(token)
      if (!token) {
        setState((current) => ({ ...current, user: null, loading: false, signedIn: false }))
        return
      }
      await loadUser()
    })()
    return () => {
      cancelled = true
    }
  }, [loadUser])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login: async (email, password) => {
        await beginSession(await authApi.login(email, password))
      },
      register: async (input) => {
        await authApi.register(input)
        // Registration does not sign anyone in: the gateway wants the email
        // verified first, and the login screen says so.
      },
      signInWithGoogleToken: async (idToken) => {
        await beginSession(await authApi.signInWithGoogle(idToken))
      },
      completeOAuthCode: async (code) => {
        await beginSession(await authApi.exchangeOAuthCode(code))
      },
      logout: async () => {
        await authApi.logout()
        impersonating.current = false
        apiClient.setAccessToken(null)
        forgetServerState()
        setState({ user: null, loading: false, signedIn: false, ...NOT_IMPERSONATING })
      },
      reload: loadUser,
      beginImpersonation: async (code) => {
        // Set *before* the exchange call, synchronously: `ImpersonatePage`
        // calls this from an effect that runs ahead of `AuthProvider`'s own
        // bootstrap effect (child-before-parent), so this flag has to be
        // true before that bootstrap's async IIFE gets a chance to run, not
        // after this function's own network round trip completes — by then
        // the race this guards against has already been lost once.
        impersonating.current = true
        let session: Awaited<ReturnType<typeof authApi.exchangeImpersonation>>
        try {
          session = await authApi.exchangeImpersonation(code)
        } catch (error) {
          // An invalid or expired code: nothing was ever impersonated, so
          // the ordinary bootstrap (and a real sign-in afterwards) must be
          // free to run again.
          impersonating.current = false
          throw error
        }
        forgetServerState()
        apiClient.setAccessToken(session.accessToken)
        setState({
          user: session.user,
          loading: false,
          signedIn: true,
          impersonatedBy: session.impersonatedBy,
          impersonationExpiresAt: session.expiresAt,
          impersonationEnded: false,
        })
      },
      endImpersonation: async () => {
        // No call to the server: the session has no refresh token to
        // revoke, and a logout would carry this host's refresh cookie —
        // which belongs to whoever is really signed in here, often the
        // staff member. Dropping the token is the whole of ending it.
        apiClient.setAccessToken(null)
        forgetServerState()
        setState({
          user: null,
          loading: false,
          signedIn: false,
          ...NOT_IMPERSONATING,
          impersonationEnded: true,
        })
      },
      impersonationToast,
      dismissImpersonationToast: () => setImpersonationToast(null),
    }),
    [state, loadUser, beginSession, forgetServerState, impersonationToast],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside an AuthProvider')
  return context
}
